import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ServicesPreview = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const ctx = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const services = [
    { image: "/assets/images/Cyber_Security.png", title: "Cybersecurity Solutions" },
    { image: "/assets/images/Cloud_Migrations.png", title: "Cloud Migration & Management" },
    { image: "/assets/images/Physical_Marketing.png", title: "Brand Identity & Strategy" },
    { image: "/assets/images/Web_Development.png", title: "Web & Mobile Development" },
    { image: "/assets/images/DC-DR_Migrations_and_Implementation_Services.png", title: "IT Infrastructure & Support" },
    { image: "/assets/images/Facility_Management_Services.png", title: "Facility Management Services" },
    { image: "/assets/images/Web_Hosting.png", title: "Digital Marketing & SEO" },
    { image: "/assets/images/Zoho_Cloud.png", title: "Zoho Cloud Solutions" },
    { image: "/assets/images/App_Development.png", title: "App Development" },
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (ctx.current) ctx.current.revert();
    ScrollTrigger.getAll().forEach((st) => st.kill());

    ctx.current = gsap.context(() => {
      const cards = cardRefs.current.filter(Boolean);
      if (!cards.length) return;

      if (isMobile) {
        gsap.set(cards, {
          opacity: 0,
          y: 40,
        });

        cards.forEach((card) => {
          gsap.to(card, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          });
        });

        return;
      }

      gsap.set(cards, {
        scale: 0.85,
        opacity: 0.3,
        y: 80,
        rotationY: 20,
        rotationZ: 5,
        zIndex: (i) => i + 1,
      });

      gsap.set(cards[0], {
        scale: 1.05,
        opacity: 1,
        y: 0,
        rotationY: 0,
        rotationZ: 0,
        zIndex: 100,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: `+=${(services.length - 1) * 120}vh`,
          scrub: 1,
          pin: ".sticky-container",
        },
      });

      cards.forEach((card, i) => {
        if (i === cards.length - 1) return;

        tl.to(card, {
          scale: 0.85,
          opacity: 0.4,
          y: 80,
          rotationY: 15,
          rotationZ: 4,
          zIndex: i + 1,
          duration: 1,
        }).to(
          cards[i + 1],
          {
            scale: 1.05,
            opacity: 1,
            y: 0,
            rotationY: 0,
            rotationZ: 0,
            zIndex: 100,
            duration: 1,
          },
          "<"
        );
      });
    }, sectionRef);

    return () => ctx.current && ctx.current.revert();
  }, [isMobile]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900 text-white"
    >
      <div style={{ minHeight: isMobile ? "auto" : `${services.length * 28}vh` }}>
        <div
          className={
            isMobile
              ? "relative py-8"
              : "sticky-container sticky top-0 h-screen flex items-center overflow-hidden"
          }
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <div className={isMobile ? "space-y-8" : "grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"}>
              
              {/* LEFT - Hidden on mobile */}
              {!isMobile && (
                <div className="space-y-6 text-center lg:text-left">
                  <h2 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold">
                    <span className="block">Explore Our</span>
                    <span className="block bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                      Premium Services
                    </span>
                  </h2>
                  <p className="text-gray-300 max-w-lg mx-auto lg:mx-0">
                    From cybersecurity to cloud and digital innovation — crafted for scale and impact.
                  </p>
                  <button
                    onClick={() => navigate("/services")}
                    className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-purple-600 px-8 py-4 rounded-full font-bold"
                  >
                    View All Services <ChevronRight />
                  </button>
                </div>
              )}
               {/* Mobile header at bottom */}
              {isMobile && (
                <div className="pt-6 text-center">
                  <h2 className="text-2xl font-bold">
                    <span className="block">Explore Our</span>
                    <span className="block bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                      Premium Services
                    </span>
                  </h2>
                    <p className="text-gray-300 max-w-lg mx-auto lg:mx-0">
                    From cybersecurity to cloud and digital innovation — crafted for scale and impact.
                  </p>
                  <button
                    onClick={() => navigate("/services")}
                    className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-600 px-6 py-3 rounded-full font-bold text-sm"
                  >
                    View All Services <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* RIGHT - Full images only */}
              <div className={isMobile ? "space-y-4" : "relative h-[80vh]"}>
                {services.map((service, index) => (
                  <div
                    key={index}
                    ref={(el) => (cardRefs.current[index] = el)}
                    className={isMobile ? "relative" : "absolute inset-0"}
                  >
                    {isMobile ? (
                      // MOBILE: Full image only
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-auto rounded-lg"
                        loading="lazy"
                      />
                    ) : (
                      // DESKTOP: Original card
                      <div className="w-full h-full rounded-3xl overflow-hidden bg-white/5 border border-white/20 shadow-2xl">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

             
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;